import { useEffect, useState } from "react";
import api from "../api/api";
import TradeIcon from "../assets/trade.svg";
import { usePortfolio } from '../context/PortfolioProvider';
import { useSocket } from '../context/SocketProvider';

export default function TradeForm({ initialCoin = null, onClose = null, isVisible = false }) {
  const [mode, setMode] = useState(null);
  const [coinList, setCoinList] = useState([]);
  const [selectedCoin, setSelectedCoin] = useState(initialCoin);
  const [dollarAmount, setDollarAmount] = useState("");
  const [coinAmount, setCoinAmount] = useState("");
  const { refresh: refreshPortfolio } = usePortfolio();
  const socket = useSocket();

  useEffect(() => {
    async function fetchTradeableCoins() {
      try {
        const response = await api.get('/trade/tradeableCoins');
        setCoinList(response.data);
        if (initialCoin) {
          const match = response.data.find((c) => c.id === initialCoin.id);
          if (match) setSelectedCoin(match);
        }
      } catch (err) {
        console.error("Error fetching tradeable coins:", err);
      }
    }
    fetchTradeableCoins();
    if (!socket) return;
    const handleCoinDataUpdated = () => {
      fetchTradeableCoins();
    };
    socket.on('coinDataUpdated', handleCoinDataUpdated);
    return () => {
      socket.off('coinDataUpdated', handleCoinDataUpdated);
    };
  }, [initialCoin, socket]);

  const handleCoinSelect = async (coinId) => {
    const coin = coinList.find(c => c.id === coinId);
    if (!coin) return;
    setSelectedCoin(coin);
    setDollarAmount("");
    setCoinAmount("");
  };

  const handleDollarChange = (e) => {
    const value = e.target.value;
    setDollarAmount(value);
    if (!isNaN(value) && value !== "" && selectedCoin) {
      setCoinAmount((parseFloat(value) / selectedCoin.current_price).toFixed(6));
    } else {
      setCoinAmount("");
    }
  };

  const handleCoinChange = (e) => {
    const value = e.target.value;
    setCoinAmount(value);
    if (!isNaN(value) && value !== "" && selectedCoin) {
      setDollarAmount((parseFloat(value) * selectedCoin.current_price));
    } else {
      setDollarAmount("");
    }
  };

  const handleTrade = async () => {
    if (!isValid) return;
    try {
      const payload = {
        coin_id: selectedCoin.id,
        type: mode,
        quantity: parseFloat(coinAmount),
      }
      await api.post('/trade', payload, { withCredentials: true });
      await refreshPortfolio();
      alert(`${mode.toUpperCase()} ${coinAmount} ${selectedCoin.symbol} for $${dollarAmount}`);
      onClose?.(); // Close modal if from mobile
      setDollarAmount("");
      setCoinAmount("");
    } catch (err) {
      console.error("Trade failed:", err);
      alert("Trade failed.");
    }
  };

  const isValid = mode && selectedCoin && (coinAmount || dollarAmount);

  return (
    <div className={`space-y-6 ${isVisible ? "" : "hidden lg:inline w-[360px] sticky top-44 p-6 border shadow-md"}`}>
      <h1 className="text-2xl font-bold">Trade</h1>

      <div className="flex justify-center space-x-4">
        <button
          className={`w-1/2 px-6 py-2 rounded-full font-bold ${
            mode === "buy" ? "bg-blue-500 text-white" : "bg-stone-200 text-stone-700"
          }`}
          onClick={() => setMode("buy")}
        >
          Buy
        </button>
        <button
          className={`w-1/2 px-6 py-2 rounded-full font-bold ${
            mode === "sell" ? "bg-blue-500 text-white" : "bg-stone-200 text-stone-700"
          }`}
          onClick={() => setMode("sell")}
        >
          Sell
        </button>
      </div>

      <div>
        <select
          className="w-full h-12 border border-stone-300 rounded-lg px-4 py-2 bg-white"
          onChange={(e) => handleCoinSelect(e.target.value)}
          value={selectedCoin ? selectedCoin.id : ""}
        >
          <option value="" disabled>Select a coin to trade</option>
          {coinList.map((coin) => (
            <option key={coin.id} value={coin.id}>
              {coin.name} ({coin.symbol.toUpperCase()})
            </option>
          ))}
        </select>
      </div>

      {selectedCoin && (
        <p className="text-center">{selectedCoin.name} Price: ${selectedCoin.current_price} / Coin</p>
      )}

      <div className="flex space-x-3 relative">
        <div className="w-1/2">
          <label className="block font-semibold text-sm text-stone-700">USD Amount</label>
          <input
            type="number"
            min="0"
            value={dollarAmount}
            onChange={handleDollarChange}
            className="w-full border border-stone-300 rounded-lg px-4 py-2 focus:outline-none focus:ring-2 focus:ring-stone-500"
            disabled={!selectedCoin}
          />
        </div>

        <img src={TradeIcon} alt="trade icon" className="relative top-2"/>

        <div className="w-1/2">
          <label className="block font-semibold text-sm text-stone-700">
            {selectedCoin ? selectedCoin.symbol.toUpperCase() : "Coin"} Amount
          </label>
          <input
            type="number"
            min="0"
            value={coinAmount}
            onChange={handleCoinChange}
            className="w-full border border-stone-300 rounded-lg px-4 py-2 focus:outline-none focus:ring-2 focus:ring-stone-500"
            disabled={!selectedCoin}
          />
        </div>
      </div>

      <button
        onClick={handleTrade}
        disabled={!isValid}
        className={`w-full py-3 rounded-full font-bold text-base transition ${
          isValid
            ? "bg-blue-500 text-white hover:bg-blue-600"
            : "bg-stone-300 text-stone-500 cursor-not-allowed"
        }`}
      >
        {mode && selectedCoin
          ? `${mode === "buy" ? "Buy" : "Sell"} ${selectedCoin.symbol.toUpperCase()}`
          : "Select Buy/Sell and Coin"}
      </button>
    </div>
  );
}